import { Box, Button, Container, Stack, Typography } from '@mui/material'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import type { ReactNode } from 'react'
import { useAuth } from './AuthProvider'
import { ADMIN_ROLES } from './roles'
import { LoadingState } from '../components/ui/States'
import Logo from '../components/Logo'

/**
 * Gate for the admin console.
 * - waits for auth to initialize
 * - if auth is disabled (dev), allows through
 * - if not signed in, shows a sign-in prompt
 * - if signed in without an admin role, shows an access-denied notice
 */
export default function RequireAdmin({ children }: { children: ReactNode }) {
  const { ready, authEnabled, isAuthenticated, login, logout, hasRole, name, roles } =
    useAuth()

  if (!ready) return <LoadingState label="Checking your access…" />

  // Dev mode (Entra not configured): the API grants a dev admin, so allow.
  if (!authEnabled) return <>{children}</>

  if (!isAuthenticated) {
    return (
      <Gate
        icon={<LockRoundedIcon />}
        title="Admin sign-in required"
        body="Sign in with your Vericast account to manage events."
        action={
          <Button variant="contained" size="large" onClick={login}>
            Sign in with Microsoft
          </Button>
        }
      />
    )
  }

  if (!hasRole(ADMIN_ROLES)) {
    return (
      <Gate
        icon={<LockRoundedIcon />}
        title="Access denied"
        body={`You're signed in as ${name}, but your account doesn't have an event-management role. Contact your administrator to request access.`}
        detail={
          roles.length
            ? `Roles detected: ${roles.join(', ')}`
            : 'No roles were found in your token. If you were just assigned a role, sign out and back in so a fresh token is issued.'
        }
        action={
          <Button variant="outlined" onClick={logout}>
            Sign out
          </Button>
        }
      />
    )
  }

  return <>{children}</>
}

function Gate({
  icon,
  title,
  body,
  detail,
  action,
}: {
  icon: ReactNode
  title: string
  body: string
  detail?: string
  action: ReactNode
}) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Container maxWidth="xs">
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Logo />
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,117,188,0.1)',
              color: 'primary.main',
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {body}
            </Typography>
            {detail && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}
              >
                {detail}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
      </Container>
    </Box>
  )
}
